import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Alert,
  FlatList,
  RefreshControl,
} from "react-native";
import * as Sentry from "@sentry/react-native";

import ScreenLoadingIcon from "../../Components/Icons/Loading/ScreenLoadingIcon";
import MediaRequestCard from "../MediaManager/Components/MediaRequestCard";

import { useHeaderContext } from "@/context/header/useHeaderContext";
import { type AppUserSession } from "@/schemas/auth/session";
import { MediaPermissionTabs, Screen } from "@/schemas/screen/tabs";
import PermissionService, {
  type PermissionRequest,
  RequestState,
  RequestType,
} from "@/services/user/permission";

interface ManageAccessScreenProps {
  session: AppUserSession;
}

const PAGINATION_LIMIT = 10;

function getRequestType(tab: MediaPermissionTabs): RequestState {
  switch (tab) {
    case MediaPermissionTabs.APPROVED:
      return RequestState.APPROVED;
    case MediaPermissionTabs.REQUESTED:
      return RequestState.INIT;
  }
}

function ManagePermissionScreen({
  session,
}: ManageAccessScreenProps): React.JSX.Element {
  const { currentScreen } = useHeaderContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<MediaPermissionTabs>(
    MediaPermissionTabs.REQUESTED,
  );
  const [requests, setRequests] = useState<Record<string, PermissionRequest>>(
    {},
  );
  const permsService = useMemo(
    () => new PermissionService(session),
    [session.token], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (currentScreen.screenId === Screen.MEDIA_PERMISSION) {
      setCurrentTab(currentScreen.activeTabId);
      fetchPermissionRequests(currentScreen.activeTabId, "loadFromBeginning");
    }
  }, [currentScreen]);

  const fetchGenRef = useRef(0);
  const fetchPermissionRequests = useCallback(
    async (
      tab: MediaPermissionTabs,
      mode: "loadFromBeginning" | "loadMore",
    ): Promise<void> => {
      if (mode === "loadMore" && nextCursor === null) return;

      const gen = ++fetchGenRef.current;

      if (mode === "loadFromBeginning") {
        setIsLoading(true);
        setRequests({});
        setNextCursor(null);
      } else if (mode === "loadMore") {
        setIsLoadingMore(true);
      }

      // prepare API call parameters
      const state = getRequestType(tab);

      const page = {
        limit: PAGINATION_LIMIT,
        next: mode === "loadMore" ? nextCursor : null,
      };

      // make API call and handle response
      try {
        const response = await permsService.getRequests(
          RequestType.PRIVATE_PHOTOS,
          state,
          page,
        );

        // Discard stale response if a newer fetch was triggered
        if (gen !== fetchGenRef.current) return;

        const requestsObject = response.items.reduce(
          (acc, request) => {
            acc[request.id] = request;
            return acc;
          },
          {} as Record<string, PermissionRequest>,
        );

        setRequests((prev) =>
          mode === "loadMore" ? { ...prev, ...requestsObject } : requestsObject,
        );
        setNextCursor(response.next);
      } catch (error) {
        if (gen !== fetchGenRef.current) return;
        Sentry.captureException(error);
        Alert.alert("Something went wrong", "Please try again later.", [
          { text: "OK" },
        ]);
      } finally {
        if (gen === fetchGenRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [nextCursor, permsService],
  );

  const removeRequest = useCallback((id: string) => {
    setRequests((prevRequests) => {
      const newRequests = { ...prevRequests };
      delete newRequests[id];
      return newRequests;
    });
  }, []);

  const onRefresh = useCallback(async () => {
    await fetchPermissionRequests(currentTab, "loadFromBeginning");
  }, [currentTab, fetchPermissionRequests]);

  const fetchMorePermissions = useCallback(
    (): Promise<void> => fetchPermissionRequests(currentTab, "loadMore"),
    [currentTab, fetchPermissionRequests],
  );

  const renderItem = ({
    item,
  }: {
    item: PermissionRequest;
  }): React.JSX.Element => {
    return (
      <MediaRequestCard
        session={session}
        request={item}
        removeItem={() => removeRequest(item.id)}
      />
    );
  };

  if (isLoading) {
    return <ScreenLoadingIcon />;
  }

  return (
    <FlatList
      data={Object.values(requests)}
      renderItem={(item) => renderItem(item)}
      keyExtractor={(item) => item.id}
      refreshControl={
        <RefreshControl refreshing={isLoading} onRefresh={onRefresh} />
      }
      onEndReached={fetchMorePermissions}
      ListFooterComponent={
        isLoadingMore ? <ActivityIndicator size="small" /> : null
      }
    />
  );
}

export default ManagePermissionScreen;
